import { useEffect, useState } from 'react'
import {
  Box,
  Card,
  CardBody,
  Grid,
  Stat,
  StatLabel,
  StatNumber,
  Skeleton,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '@chakra-ui/react'
import ReportPageHeader from '@/components/reports/ReportPageHeader'
import { helpdeskReportService, type ReportFilters } from '@/lib/services/helpdeskReportService'

export default function SlaCompliancePage() {
  const [filters, setFilters] = useState<ReportFilters>({})
  const [parentData, setParentData] = useState<{
    summary: Record<string, number>
    tickets: Array<Record<string, string>>
  } | null>(null)
  const [taskData, setTaskData] = useState<{
    summary: Record<string, number>
    tasks: Array<Record<string, string>>
  } | null>(null)
  const [loadingParent, setLoadingParent] = useState(true)
  const [loadingTasks, setLoadingTasks] = useState(true)

  useEffect(() => {
    setLoadingParent(true)
    helpdeskReportService
      .getSla(filters)
      .then(setParentData)
      .finally(() => setLoadingParent(false))
  }, [filters.from, filters.to, filters.client_id])

  useEffect(() => {
    setLoadingTasks(true)
    helpdeskReportService
      .getTasksReport(filters)
      .then(setTaskData)
      .finally(() => setLoadingTasks(false))
  }, [filters.from, filters.to, filters.client_id])

  const parentSummary = parentData?.summary
  const taskSummary = taskData?.summary

  return (
    <Box p={4}>
      <ReportPageHeader
        title="SLA Compliance"
        subtitle="Resolution and task SLA performance"
        reportType="sla"
        filters={filters}
        onFiltersChange={setFilters}
      />

      <Tabs colorScheme="blue" variant="line">
        <TabList mb={4}>
          <Tab fontWeight="600">Tickets</Tab>
          <Tab fontWeight="600">Tasks</Tab>
        </TabList>

        <TabPanels>
          <TabPanel px={0}>
            <Grid templateColumns={{ base: '1fr 1fr', lg: 'repeat(4, 1fr)' }} gap={4} mb={6}>
              {loadingParent ? (
                Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} height="80px" />)
              ) : (
                <>
                  <Card><CardBody><Stat><StatLabel>Within SLA %</StatLabel><StatNumber>{parentSummary?.resolution_within_sla_percent ?? 0}%</StatNumber></Stat></CardBody></Card>
                  <Card><CardBody><Stat><StatLabel>Breached</StatLabel><StatNumber>{parentSummary?.breached_count ?? 0}</StatNumber></Stat></CardBody></Card>
                  <Card><CardBody><Stat><StatLabel>Avg resolution (hrs)</StatLabel><StatNumber>{parentSummary?.avg_resolution_hours ?? 0}</StatNumber></Stat></CardBody></Card>
                  <Card><CardBody><Stat><StatLabel>Tickets with SLA</StatLabel><StatNumber>{parentSummary?.total_with_sla ?? 0}</StatNumber></Stat></CardBody></Card>
                </>
              )}
            </Grid>

            <Card>
              <CardBody overflowX="auto">
                {loadingParent ? (
                  <Skeleton height="200px" />
                ) : (
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>Ticket #</Th>
                        <Th>Subject</Th>
                        <Th>Due by</Th>
                        <Th>Resolved</Th>
                        <Th>Status</Th>
                        <Th>Reason</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {(parentData?.tickets ?? []).map((t) => (
                        <Tr key={t.ticket_id}>
                          <Td>{t.ticket_number}</Td>
                          <Td>{t.subject}</Td>
                          <Td>{t.due_by_at ? new Date(t.due_by_at).toLocaleString() : '—'}</Td>
                          <Td>{t.resolved_at ? new Date(t.resolved_at).toLocaleString() : '—'}</Td>
                          <Td>{t.resolution_status || '—'}</Td>
                          <Td>{t.breach_reason || '—'}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </TabPanel>

          <TabPanel px={0}>
            <Grid templateColumns={{ base: '1fr 1fr', lg: 'repeat(4, 1fr)' }} gap={4} mb={6}>
              {loadingTasks ? (
                Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} height="80px" />)
              ) : (
                <>
                  <Card><CardBody><Stat><StatLabel>Within SLA %</StatLabel><StatNumber>{taskSummary?.within_sla_percent ?? 0}%</StatNumber></Stat></CardBody></Card>
                  <Card><CardBody><Stat><StatLabel>Breached</StatLabel><StatNumber>{taskSummary?.breached_count ?? 0}</StatNumber></Stat></CardBody></Card>
                  <Card><CardBody><Stat><StatLabel>Avg completion (hrs)</StatLabel><StatNumber>{taskSummary?.avg_completion_hours ?? 0}</StatNumber></Stat></CardBody></Card>
                  <Card><CardBody><Stat><StatLabel>Tasks with SLA</StatLabel><StatNumber>{taskSummary?.total_with_sla ?? 0}</StatNumber></Stat></CardBody></Card>
                </>
              )}
            </Grid>

            <Card>
              <CardBody overflowX="auto">
                {loadingTasks ? (
                  <Skeleton height="200px" />
                ) : (
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>Task #</Th>
                        <Th>Title</Th>
                        <Th>Ticket</Th>
                        <Th>Due by</Th>
                        <Th>Completed</Th>
                        <Th>Status</Th>
                        <Th>Reason</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {(taskData?.tasks ?? []).map((t) => (
                        <Tr key={t.task_id}>
                          <Td>{t.task_number}</Td>
                          <Td>{t.title || '—'}</Td>
                          <Td>{t.parent_ticket_number || t.parent_ticket_id || '—'}</Td>
                          <Td>{t.sla_due_at ? new Date(t.sla_due_at).toLocaleString() : '—'}</Td>
                          <Td>{t.actual_end ? new Date(t.actual_end).toLocaleString() : '—'}</Td>
                          <Td>{t.sla_status || '—'}</Td>
                          <Td>{t.breach_reason || '—'}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  )
}
